import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useApp } from "@/context/AppContext";
import { daysUntil, formatDeadline } from "@/lib/mockData";
import { CalendarDays, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Full-width horizontal ribbon showing upcoming deadlines on a 90-day axis.
 * Dots are colour-coded by urgency and clickable — each jumps to the right
 * application detail.
 */
const AXIS_DAYS = 90;
const TICKS = [0, 7, 14, 30, 60, 90];

export default function DeadlineStrip() {
    const { scoredGrants, applications } = useApp();

    const items = useMemo(() => {
        const appMap = new Map();
        for (const a of applications) appMap.set(a.target_id, a);

        const out = [];
        // Active applications (only surface if they have a deadline we can parse).
        for (const a of applications) {
            const raw = a.deadline || a.target_meta?.close_date || a.target_meta?.deadline;
            const d = daysUntil(raw);
            if (d === null || d < -7 || d > AXIS_DAYS) continue;
            out.push({
                id: `a-${a.id}`,
                title: a.target_title || "Application",
                days: d,
                deadline: raw,
                href: `/app/detail/${a.type}/${a.target_id}`,
                active: true,
                progress: a.progress || 0,
            });
        }
        // Top curated grants inside the window, excluding ones already above.
        for (const g of scoredGrants) {
            if (!g.deadline || appMap.has(g.id)) continue;
            const d = daysUntil(g.deadline);
            if (d === null || d < 0 || d > AXIS_DAYS) continue;
            out.push({
                id: `g-${g.id}`,
                title: g.title,
                days: d,
                deadline: g.deadline,
                href: `/app/detail/grant/${g.id}`,
                active: false,
                progress: 0,
            });
        }
        return out.sort((a, b) => a.days - b.days).slice(0, 18);
    }, [scoredGrants, applications]);

    if (items.length === 0) return null;

    return (
        <Card className="mt-6 p-5 sm:p-6 border-border" data-testid="deadline-strip">
            <div className="flex items-end justify-between flex-wrap gap-2">
                <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Next 90 days</p>
                    <h3 className="mt-1 font-serif text-xl sm:text-2xl leading-tight inline-flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-muted-foreground" /> Deadline horizon
                    </h3>
                </div>
                <div className="flex items-center gap-3 text-[10px] font-mono text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-destructive" /> 7d</span>
                    <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-terracotta" /> 30d</span>
                    <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-primary" /> 90d</span>
                </div>
            </div>

            <TooltipProvider delayDuration={100}>
                <div className="mt-6 relative h-16 select-none">
                    {/* Axis line */}
                    <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-border" />
                    {/* Tick marks */}
                    {TICKS.map((t) => (
                        <div key={t} className="absolute top-1/2 -translate-y-1/2" style={{ left: `${(t / AXIS_DAYS) * 100}%` }}>
                            <div className="h-2 w-px bg-border mx-auto" />
                            <p className="mt-1.5 text-[9px] font-mono text-muted-foreground whitespace-nowrap -translate-x-1/2 relative">{t === 0 ? "today" : `${t}d`}</p>
                        </div>
                    ))}
                    {/* Dots */}
                    {items.map((it) => {
                        const pct = Math.max(0, Math.min(100, (it.days / AXIS_DAYS) * 100));
                        const urg = urgency(it.days);
                        return (
                            <Tooltip key={it.id}>
                                <TooltipTrigger asChild>
                                    <Link
                                        to={it.href}
                                        aria-label={it.title}
                                        data-testid={`deadline-dot-${it.id}`}
                                        className={cn(
                                            "absolute top-1/2 -translate-y-1/2 h-3.5 w-3.5 rounded-full border-2 border-background shadow-soft transition-transform hover:scale-150",
                                            urg.dot,
                                            it.active && "ring-2 ring-offset-1 ring-offset-card ring-accent"
                                        )}
                                        style={{ left: `calc(${pct}% - 7px)` }}
                                    />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-[260px]">
                                    <p className="font-serif text-sm leading-tight">{it.title}</p>
                                    <p className="text-[10px] text-muted-foreground mt-1 font-mono uppercase tracking-wider">
                                        {it.days <= 0 ? `due today` : `${it.days} day${it.days === 1 ? "" : "s"} · ${formatDeadline(it.deadline)}`}
                                    </p>
                                    {it.active && <p className="text-[10px] text-accent-foreground mt-1">In your applications</p>}
                                </TooltipContent>
                            </Tooltip>
                        );
                    })}
                </div>
            </TooltipProvider>
        </Card>
    );
}

function urgency(d) {
    if (d <= 7) return { dot: "bg-destructive", label: "This week" };
    if (d <= 30) return { dot: "bg-terracotta", label: "This month" };
    return { dot: "bg-primary", label: "Later" };
}
